import { listClasses, paperClasses } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import { t } from 'i18next';
import React, { ReactElement, useCallback, useMemo } from 'react';

import { JobAssignmentType } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import InfiniteScrollLoadingIndicator from '~pages/Dispatch/components/InfiniteScrollLoadingIndicator';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

import {
  FILTER_MENU_ITEM_HEIGHT_IN_PX,
  FilterMenuItemLoadingReason,
} from '../../Filters/FilterMenuItem';
import Menu from '../../Menu/Menu';
import { SearchBar } from '../../Search/SearchBar';
import Tabs from '../../Tabs/Tabs';
import { DriverMenuItem } from './DriverMenuItem';
import useAssignDriverMenu, { SelectedAssignment } from './hooks/useAssignDriverMenu';

const tabs = [
  {
    dataUi: 'page-dispatch__assign-popover__driver-tab',
    label: t('dispatch.job.assignment_popover.driver'),
    value: JobAssignmentType.DRIVER,
  },
  {
    dataUi: 'page-dispatch__assign-popover__vendor-tab',
    label: t('dispatch.job.assignment_popover.vendor'),
    value: JobAssignmentType.VENDOR,
  },
];

interface AssignDriverMenuProps {
  companyId: string;
  customAssignCTA?: ReactElement;
  filterDate?: string;
  onChange: (assignment: SelectedAssignment) => void;
  onOpenStateChanged: (open: boolean) => void;
}

export function AssignDriverMenu({
  companyId,
  customAssignCTA,
  filterDate,
  onOpenStateChanged,
  onChange,
}: AssignDriverMenuProps) {
  const theme = useTheme();

  const hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.hideDriversVendorNameIfOwnerOperator,
  });

  const switchAssigneeTabsOrderFeatureFlagEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.switchAssigneeTabsOrder,
  });

  const sortedTabs = useMemo(() => {
    if (switchAssigneeTabsOrderFeatureFlagEnabled) {
      return [...tabs].reverse();
    }

    return tabs;
  }, [switchAssigneeTabsOrderFeatureFlagEnabled]);

  const {
    applyDebouncedFilter,
    closeMenu,
    data,
    fetchMore,
    hasMore,
    isMenuOpen,
    loadingReason,
    openMenu,
    searchBarRef,
    selectedTab,
    setSearchFieldValue,
    setSelectedTab,
  } = useAssignDriverMenu(
    companyId,
    switchAssigneeTabsOrderFeatureFlagEnabled ? JobAssignmentType.VENDOR : undefined,
    filterDate,
  );

  const AssignCTA = customAssignCTA ? (
    React.cloneElement(customAssignCTA, {
      onClick(event: React.MouseEvent<HTMLElement>) {
        event?.stopPropagation();
      },
      sx: () => ({
        ...(customAssignCTA.props.sx || {}),
        backgroundColor: isMenuOpen ? theme.brandV2.colors.treadOrange : 'inherit',
        '& > p': { color: isMenuOpen ? theme.palette.common.white : 'inherit' },
      }),
    })
  ) : (
    <Button color="primary">{t('dispatch.job.assign_to')}</Button>
  );

  const handleOpenStateChanged = useCallback(
    (open: boolean) => {
      onOpenStateChanged(open);

      if (open) {
        openMenu();
      } else {
        closeMenu();
      }
    },
    [onOpenStateChanged, openMenu, closeMenu],
  );

  return (
    <Menu
      menuTrigger={AssignCTA}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      onClick={(event: React.MouseEvent) => {
        event.stopPropagation();
      }}
      onOpenStateChanged={handleOpenStateChanged}
      sx={{
        [`& .${paperClasses.root}`]: { minWidth: '350px' },
        [`& .${listClasses.root}`]: {
          display: 'flex',
          flexDirection: 'column',
          padding: '12px',
        },
      }}
    >
      <Tabs
        onChange={(tab) => setSelectedTab(tab)}
        selected={selectedTab}
        tabs={sortedTabs}
        sx={{ paddingBottom: '12px' }}
      />
      <SearchBar
        ref={searchBarRef}
        dataUi="page-dispatch__assign-popover__search-input"
        focusOnMount={true}
        placeHolder={
          selectedTab === JobAssignmentType.VENDOR
            ? t('dispatch.job.assignment_popover.search_for_vendor') || ''
            : t('dispatch.job.assignment_popover.search_for_driver') || ''
        }
        alwaysExpanded
        isLoading={loadingReason === FilterMenuItemLoadingReason.SEARCH_VALUE}
        setFilter={(value) => {
          applyDebouncedFilter(() => setSearchFieldValue(value));
        }}
      />
      <Divider sx={{ '&.MuiDivider-root': { mt: '8px', mb: 0 } }} />
      <Box
        sx={{
          overflow: 'auto',
          height: `${FILTER_MENU_ITEM_HEIGHT_IN_PX * 9}px`,
        }}
      >
        {(data ?? []).map((item, index) => (
          <React.Fragment key={item.id}>
            <MenuItem
              data-ui={`page-dispatch__assign-popover__${item.type === JobAssignmentType.VENDOR ? 'vendor' : 'driver'}-${item.id}`}
              onClick={(event) => {
                event.stopPropagation();
                onChange(item);
              }}
              sx={{
                '&.MuiMenuItem-root': { padding: '0px' },
                '&:hover': { backgroundColor: 'transparent' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                }}
              >
                <DriverMenuItem
                  filterDate={filterDate}
                  entity={item.entity}
                  hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled={
                    hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled
                  }
                />
              </Box>
            </MenuItem>

            {index === (data ?? []).length - 1 && hasMore && (
              <InfiniteScrollLoadingIndicator
                alignItems="center"
                display="flex"
                height={FILTER_MENU_ITEM_HEIGHT_IN_PX}
                isLoading={loadingReason === FilterMenuItemLoadingReason.INFINITE_SCROLL}
                justifyContent="center"
                onFetchMore={fetchMore}
              />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Menu>
  );
}
